import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useMemo, useState } from "react";
import { AgentAvatar } from "./AgentAvatar.js";
import {
	activityHistoryFor,
	liveStatusFor,
	type AgentLiveStatus,
} from "./agent-activity.js";
import { AGENTS, type AgentId } from "./nav.js";
import { cn } from "./cn.js";

type ActivityFilter = "all" | "running" | "waiting" | "idle";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	setupDone: boolean;
	onboardingStep?: string;
	onOpenAgent: (id: AgentId) => void;
};

function matchesFilter(
	state: AgentLiveStatus["state"],
	filter: ActivityFilter,
): boolean {
	switch (filter) {
		case "all":
			return true;
		case "running":
			return state === "running";
		case "waiting":
			return state === "waiting";
		case "idle":
			return state === "idle";
		default: {
			const _exhaustive: never = filter;
			return _exhaustive;
		}
	}
}

function stateBadge(state: AgentLiveStatus["state"]): string {
	switch (state) {
		case "running":
			return "Running";
		case "waiting":
			return "Waiting";
		case "idle":
			return "Idle";
		case "locked":
			return "Locked";
		default: {
			const _exhaustive: never = state;
			return _exhaustive;
		}
	}
}

/** Workspace-wide agent activity — stubbed from liveStatusFor until DO streams. */
export function ActivityModal({
	open,
	onOpenChange,
	setupDone,
	onboardingStep,
	onOpenAgent,
}: Props) {
	const [filter, setFilter] = useState<ActivityFilter>("all");

	const rows = useMemo(() => {
		return AGENTS.filter((a) => a.availability === "live").map((agent) => {
			const live = liveStatusFor(agent.id, setupDone, onboardingStep);
			const trail = activityHistoryFor(agent.id, live.state);
			return { agent, live, trail };
		});
	}, [setupDone, onboardingStep]);

	const visible = rows.filter((row) =>
		matchesFilter(row.live.state, filter),
	);

	const runningCount = rows.filter((r) => r.live.state === "running").length;
	const waitingCount = rows.filter((r) => r.live.state === "waiting").length;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="activity-modal gap-0 p-0 sm:max-w-lg">
				<DialogHeader className="gap-2 border-b border-mx-sep px-6 pt-6 pb-4 text-left">
					<DialogTitle className="text-xl tracking-[-0.02em]">
						Live activity
					</DialogTitle>
					<DialogDescription className="text-[13px] text-mx-secondary">
						{runningCount} running · {waitingCount} waiting across your
						team.
					</DialogDescription>
					<div
						className="agents-filter mt-1"
						role="group"
						aria-label="Filter activity"
					>
						{(
							[
								["all", "All"],
								["running", "Running"],
								["waiting", "Waiting"],
								["idle", "Idle"],
							] as const
						).map(([id, label]) => (
							<button
								key={id}
								type="button"
								className="agents-filter-btn"
								aria-pressed={filter === id}
								onClick={() => setFilter(id)}
							>
								{label}
							</button>
						))}
					</div>
				</DialogHeader>

				<div className="activity-modal-body">
					{visible.length === 0 ? (
						<p className="agents-hub-empty m-4">
							No agents in this filter.
						</p>
					) : (
						<ul className="activity-list">
							{visible.map(({ agent, live, trail }) => (
								<li key={agent.id}>
									<button
										type="button"
										className={cn(
											"activity-row",
											live.state === "running" &&
												"activity-row--running",
											live.state === "waiting" &&
												"activity-row--waiting",
										)}
										onClick={() => {
											onOpenChange(false);
											onOpenAgent(agent.id);
										}}
									>
										<span className="side-item-mark">
											<AgentAvatar agent={agent} size="sm" />
										</span>
										<span className="activity-row-copy">
											<span className="activity-row-title">
												<span className="side-item-name">
													{agent.name}
												</span>
												<Badge
													variant="outline"
													className="text-[10px]"
												>
													{stateBadge(live.state)}
												</Badge>
											</span>
											<span className="activity-row-summary">
												{live.summary}
											</span>
											{trail.length > 0 && (
												<span className="activity-row-trail">
													{trail[trail.length - 1]?.title}
												</span>
											)}
										</span>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>

				<div className="flex justify-end border-t border-mx-sep px-6 py-3">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => onOpenChange(false)}
					>
						Close
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

/** Count of live agents in running/waiting — for the sidebar badge. */
export function activeActivityCount(
	setupDone: boolean,
	onboardingStep?: string,
): number {
	return AGENTS.filter((a) => a.availability === "live").filter((a) => {
		const state = liveStatusFor(a.id, setupDone, onboardingStep).state;
		return state === "running" || state === "waiting";
	}).length;
}
